import {
  Flex,
  IconButton,
  Stack,
  useDisclosure
} from '@chakra-ui/react';
import {
  UiCard,
  UiText,
  UiIconShoppingCart,
  UiIconList,
  UiHStack,
  UiStack,
  uiStyles,
  UiSpinner, UiIconHouse, UiDrawer, UiDrawerOverlay, UiDrawerContent, UiDrawerCloseButton, UiDrawerBody, type UiHStackProps,
} from '@/lib/ui';
import ShoppingCartDrawer from '@/registration/component/Register/GroupShoppingCart/Drawer';
import { emptyShoppingCart } from '@/api/registration';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { type ElementType, type FC, useEffect, useMemo } from 'react';
import { useTenantRoute } from '@/account/hook/useTenantRoute';
import { generatePageUrl } from '@/app/pages';
import BaseBlockList from '@/base/BlockList';
import BaseRouterLink from '@/base/Router/Link';
import { topNavHeight } from '@/base/Layout/viewport';
import { useNavigate } from 'react-router-dom';
import { useIsGroupEmptyRegisTypeInCart } from '@/registration/hook/useIsGroupEmptyRegisTypeInCart';
import { useRegisterGroupSteps } from '@/registration/hook/useRegisterGroupSteps';
import { useShoppingCartToggle } from '@/registration/component/Register/ShoppingCart/ShoppingCartToggleProvider';
import LocaleSwitch from '@/registration/component/Locale/LocaleSwitch';

const NO_REDIRECT_URLS = [
  'RegistrationRegisterGroupCategory',
  'RegistrationRegisterGroupAttendees',
  'RegistrationRegisterGroupMainTickets',
  'RegistrationRegisterInviteeTermsPrivacy',
  'RegistrationRegisterLandingPage'
];

function GroupHeader() {
  const { isOpen, onToggle } = useDisclosure();
  // const { isOpen: isShoppingCartOpen, onToggle: onShoppingCartToggle } = useDisclosure();
  const { isOpen: isShoppingCartOpen, onToggle: onShoppingCartToggle } = useShoppingCartToggle();
  const { eventId, tenantCode } = useRegisterRoute();
  const navigate = useNavigate();
  const { currentStep } = useRegisterGroupSteps();

  const { data: shoppingCart = emptyShoppingCart, isLoading: isShoppingCartLoading, isError } = useGroupCartQuery(eventId);
  const { isGroupEmptyMainTicketInCart } = useIsGroupEmptyRegisTypeInCart(eventId);

  const isShoppingCartEmpty = useMemo(
    () => {
      return !shoppingCart.cartItems?.length || !Array.isArray(shoppingCart.cartItems) || shoppingCart.cartItems.length === 0;
    },
    [
      shoppingCart.cartItems
    ]
  );

  useEffect(() => {
    if (currentStep?.routeName && isGroupEmptyMainTicketInCart && !NO_REDIRECT_URLS.includes(currentStep?.routeName)) {
      navigate(generatePageUrl('RegistrationRegisterGroupMainTickets', { tenantCode, eventId }));
    }
  }, [currentStep?.routeName, eventId, isGroupEmptyMainTicketInCart, navigate, tenantCode]);

  if (isError) {
    return (
      <BaseMessageBarError>
        Failed to load the shopping cart.
      </BaseMessageBarError>
    );
  }

  return (
    <>
      <UiStack
        top={0}
        left={0}
        flexGrow={1}
        bgColor={'transparent'}
      >
        <Flex
          color={'blackAlpha.600'}
          minH={`${topNavHeight}px`}
          maxH={`${topNavHeight}px`}
          py={{ base: 4 }}
          px={{ base: 4, lg: 8 }}
          align={'center'}
        >
          <Flex
            flex={{ base: 1, md: 'auto' }}
          >
            <IconButton
              onClick={onToggle}
              // icon={
              //   isOpen ? <UiIconX size={'3xl'} /> : <UiIconList size={'3xl'} />
              // }
              icon={(
                <UiIconList size={'3xl'} />
              )}
              variant={'ghost'}
              aria-label={'Toggle Navigation'}
            />
          </Flex>
          <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }} align={'center'} />
          <Stack
            flex={{ base: 1, md: 0 }}
            justify={'flex-end'}
            direction={'row'}
            spacing={6}
            alignItems={'center'}
          >
            {/* <Button
              as={'a'}
              fontSize={'sm'}
              fontWeight={400}
              variant={'link'}
              href={'#'}>
              Sign In
            </Button> */}
            {/* <UiButtonCard
              icon={(
                <UiIconShoppingCart
                  size={'2xl'}
                  color={'text.primary'}
                />
              )}
            >
              5
            </UiButtonCard>
            <UiButtonIcon
              aria-label='Account'
              icon={(<UiIconUser color={'#FFF'} size={'2xl'}/>)}
              // backgroundColor={'primary.dark'}
              colorScheme={'primary'}
            /> */}
            <UiCard
              p={2}
              pl={4}
              pr={4}
              bgColor={'#fff'}
              enableShadow={false}
            >
              <UiHStack spacing={{base: 4, lg: 8}}>
                <LocaleSwitch
                  eventId={eventId}
                  tenantCode={tenantCode}
                  isGroup={true}
                />
                <UiHStack pl={0} {...uiStyles.hover} onClick={onShoppingCartToggle}>
                  {isShoppingCartLoading ? (<UiSpinner size='md' />) : (
                    <>
                      <UiIconShoppingCart
                        size={'3xl'}
                        color={isShoppingCartEmpty ? 'text.primary' : 'primary.500'}
                        weight={isShoppingCartEmpty ? 'regular' : 'bold'}
                      />
                      <UiText
                        variant={isShoppingCartEmpty ? 'body1' : 'title'}
                        color={isShoppingCartEmpty ? 'text.primary' : 'primary.500'}
                      >
                        {shoppingCart.cartItems.length}
                      </UiText>
                    </>)}
                </UiHStack>
              </UiHStack>
            </UiCard>
          </Stack>
        </Flex>
      </UiStack>
      <MenuDrawer isOpen={isOpen} onClose={onToggle} />
      <ShoppingCartDrawer isOpen={isShoppingCartOpen} onClose={onShoppingCartToggle} />
    </>
  );
}

interface MenuDrawerProps {
  isOpen: boolean
  onClose: () => void
}

interface MenuItem {
  label: string
  description?: string
  url?: string
  Icon?: ElementType
}

const MenuDrawer = ({
  onClose = () => { },
  isOpen = false
}: MenuDrawerProps) => {
  const { eventId } = useRegisterRoute();
  const { tenant } = useTenantRoute();

  const menuList: MenuItem[] = useMemo(
    () => {
      if (tenant?.code && eventId) {
        return [
          {
            label: 'Event home',
            description: 'Beginning of the registration',
            url: generatePageUrl('RegistrationRegisterEmail', { tenantCode: tenant?.code, eventId }),
            Icon: UiIconHouse,
          }
        ];
      }
      return [];
    },
    [
      tenant?.code,
      eventId,
    ]
  );

  return (
    <UiDrawer
      placement={'left'}
      size={'lg'}
      isOpen={isOpen}
      onClose={onClose}
    >
      <UiDrawerOverlay />
      <UiDrawerContent>
        <UiDrawerCloseButton size={'lg'} color={'primary.500'} />
        <UiDrawerBody px={{ base: 4, lg: 8 }} bgColor={'gray.100'} py={16}>
          <BaseBlockList bgColor={'#fff'}>
            {menuList.map((menuItem, index) => {
              return (
                <MenuDrawerItem key={index} data={menuItem} />
              );
            })}
          </BaseBlockList>
        </UiDrawerBody>
      </UiDrawerContent>
    </UiDrawer>
  );
};

interface MenuDrawerItemProps extends UiHStackProps {
  data: MenuItem
  isActive?: boolean
}

const MenuDrawerItem: FC<MenuDrawerItemProps> = ({
  data,
  isActive = false,
  ...props
}) => {
  // const { isOpen, onToggle } = useDisclosure();
  return (
    <BaseRouterLink to={data.url ?? '#'}>
      <UiHStack spacing={4} {...props} px={{ base: 4, lg: 8 }} py={4}>
        {!!data.Icon && (
          <data.Icon size={'3xl'} />
        )}
        <UiStack spacing={0}>
          <UiText variant={'body1'} fontWeight={600}>{data.label}</UiText>
          {!!data.description && (
            <UiText variant={'body2'} color={'text.secondary'}>{data.description}</UiText>
          )}
        </UiStack>
      </UiHStack>
    </BaseRouterLink>
  );
};

export default GroupHeader;
